import {
  Button,
  DialogModal,
  Drawer,
  Grid,
  Loader,
  Panel,
  PanelHeader,
  Tabs,
} from "@pushpress/shared-components";
import { useMutation } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  pathAppointmentType,
  postAppointmentType,
} from "../../../apis/appointmentApi";

import { AppContext } from "../../../context/app";
import { useToggle } from "../../../hooks/state";
import { extractError } from "../../../utils/misc";
import { defaultToastOptions } from "../AppointmentsUtils";
import AppointmentTypeDetails from "./AppointmentTypeDetails";
import { AppointmentTypeForm } from "./AppointmentTypeDrawer.types";
import {
  addDefaultValues,
  appointmentTypeFormDefault,
  convertToAppointmentType,
  convertToAppointmentTypeForm,
} from "./AppointmentTypeDrawer.utils";
import AppointmentTypeNotifications from "./AppointmentTypeNotifications";
import AppointmentTypeSettings from "./AppointmentTypeSettings";
import {
  useGetAppointmentTypeItemQuery,
  useGetStaffForAppointmentTypeQuery,
} from "./AppointmentTypeDrawer-generated.hooks";
import styles from "./AppointmentTypeDrawer.module.scss";

interface AppointmentTypeDrawerProps {
  visible: boolean;
  duplicate: boolean;
  appointmentTypeUuid?: string;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

function AppointmentTypeDrawer(props: AppointmentTypeDrawerProps) {
  const { visible } = props;
  return (
    <Drawer
      visible={visible}
      anchor="right"
      width="420px"
      overflow="visible"
      disableClickAway
      disableEscapeKeyDown
    >
      <AppointmentTypeDrawerContent {...props} />
    </Drawer>
  );
}

function AppointmentTypeDrawerContent(props: AppointmentTypeDrawerProps) {
  const { appointmentTypeUuid, duplicate, onClose, onSuccess } = props;

  const app = useContext(AppContext);

  const [saving, savingActions] = useToggle(false);
  const [activeTab, setActiveTab] = useState(0);
  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);

  const appointmentTypeQuery = useGetAppointmentTypeItemQuery({
    variables: {
      uuid: appointmentTypeUuid!,
    },
    skip: !appointmentTypeUuid,
    fetchPolicy: "no-cache",
  });

  const staffQuery = useGetStaffForAppointmentTypeQuery({
    fetchPolicy: "no-cache",
  });

  const pathAppointmentTypeMutation = useMutation(pathAppointmentType);

  const postAppointmentTypeMutation = useMutation(postAppointmentType);

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    trigger,
    formState: { isDirty },
  } = useForm<AppointmentTypeForm>({
    mode: "onTouched",
    defaultValues: appointmentTypeUuid ? undefined : appointmentTypeFormDefault,
  });

  const handleClose = () => {
    if (isDirty) {
      setConfirmCloseVisible(true);
    } else {
      onClose();
    }
  };

  const save = async (values: AppointmentTypeForm) => {
    const payload = convertToAppointmentType(values)!;
    savingActions.on();
    try {
      if (appointmentTypeUuid && !duplicate) {
        await pathAppointmentTypeMutation.mutateAsync({
          clientUuid: app.client!.uuid,
          appointmentTypeId: appointmentTypeUuid,
          payload,
        });
      } else {
        await postAppointmentTypeMutation.mutateAsync({
          clientUuid: app.client!.uuid,
          payload,
        });
      }
      toast.success("Saved successfully", defaultToastOptions);
      onSuccess();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      savingActions.off();
    }
  };

  useEffect(() => {
    if (!appointmentTypeQuery.data) {
      return;
    }
    const data = convertToAppointmentTypeForm(
      appointmentTypeQuery.data.getAppointmentTypeItem,
      duplicate,
    );
    if (data) {
      reset(addDefaultValues(data));
    }
  }, [appointmentTypeQuery.data, reset, duplicate]);

  const isLoading =
    appointmentTypeUuid && (!appointmentTypeQuery.data || !staffQuery.data);
  const staff = staffQuery.data?.getStaff || [];

  return (
    <>
      <Panel fullHeight>
        <Panel.Header>
          <PanelHeader title="Appointment type">
            <PanelHeader.ActionMenu>
              <Button
                text="Close"
                buttonType="secondary"
                size="medium"
                onClick={handleClose}
              />
              <Button
                text="Save"
                buttonType="primary"
                size="medium"
                type="button"
                loading={saving}
                onClick={handleSubmit(save)}
              />
            </PanelHeader.ActionMenu>
          </PanelHeader>
        </Panel.Header>
        <Panel.Body>
          {isLoading ? (
            <Grid container alignItems="center" justifyContent="center">
              <Grid item>
                <Loader className="mt-4" />
              </Grid>
            </Grid>
          ) : (
            <form
              onSubmit={handleSubmit(save)}
              className={styles.form}
              noValidate
            >
              <Tabs
                tabHeaders={["Details", "Settings", "Notifications"]}
                selectedIndex={activeTab}
                hideWithCss
                align="left"
                fullHeight
                scrollContent
                onTabClick={setActiveTab}
                onTabFocus={setActiveTab}
              >
                <AppointmentTypeDetails
                  control={control}
                  setValue={setValue}
                  trigger={trigger}
                  staff={staff}
                />
                <AppointmentTypeSettings control={control} />
                <AppointmentTypeNotifications control={control} />
              </Tabs>
            </form>
          )}
        </Panel.Body>
      </Panel>
      <DialogModal
        title="Discard unsaved changes?"
        visible={confirmCloseVisible}
        primaryBtnTitle="Discard"
        secondaryBtnTitle="Go back"
        primaryHandler={onClose}
        secondaryHandler={() => setConfirmCloseVisible(false)}
        closeModal={() => setConfirmCloseVisible(false)}
      />
    </>
  );
}

export default AppointmentTypeDrawer;
